import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationSkipped,
  Router,
} from '@angular/router';

import { filter, merge } from 'rxjs';

import { Trip } from 'src/app/shared/classes/trip';
import { ERoutes } from 'src/app/core/enums/routes.enum';
import { ITripRoute } from 'src/app/core/types/interfaces/popular-trip.interface';

import { TripsService } from 'src/app/core/services/trips.service';
import { BreakpointService } from 'src/app/core/services/breakpoint.service';
import { CarRentalService } from '../car-rental/services/car-rental.service';
import { MetaTagsConfigService } from 'src/app/core/services/meta-tags-config.service';
import { TripWizardStateService } from 'src/app/shared/components/trip-wizard/trip-wizard-state.service';

@Component({
  selector: 'app-new-trip-planner-view',
  templateUrl: './new-trip-planner.component.html',
  styleUrls: ['./new-trip-planner.component.scss'],
  standalone: false,
})
export class NewTripPlannerViewComponent implements OnInit, OnDestroy {
  public trip: WritableSignal<Trip>;
  public isWelcomeViewNeed: boolean = true;
  public visibleTripId: string | null = null;

  #router: Router = inject(Router);
  #route: ActivatedRoute = inject(ActivatedRoute);
  #tripsService: TripsService = inject(TripsService);
  #carRentalService: CarRentalService = inject(CarRentalService);
  #metaTagsConfigService: MetaTagsConfigService = inject(MetaTagsConfigService);
  #tripWizardStateService: TripWizardStateService = inject(
    TripWizardStateService,
  );

  constructor(public readonly breakpointService: BreakpointService) {
    merge(
      this.#router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
      ),
      this.#router.events.pipe(
        filter((event) => event instanceof NavigationSkipped),
      ),
    )
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.checkRouterActivationLink());
  }

  ngOnInit(): void {
    this.checkForWelcomeViewNeeded();
  }

  ngOnDestroy(): void {
    this.#tripWizardStateService.resetHistoryStack();
    this.#carRentalService.removeMeiliDirect();
  }

  needToRefreshTripStateHandler(): void {
    this.initTripData();
  }

  private initTripData(): void {
    this.trip = signal(this.#tripWizardStateService.currentTripStateValue);
  }

  private checkForWelcomeViewNeeded(): void {
    const { startingLabel, endingLabel, route } = this.trip();
    this.isWelcomeViewNeed = !startingLabel && !endingLabel && !route.length;
  }

  private checkRouterActivationLink(): void {
    const routeFirstChild: ActivatedRoute = this.#route.firstChild;
    this.initTripData();
    if (!routeFirstChild) {
      this.checkForWelcomeViewNeeded();
      this.#metaTagsConfigService.setTripPlannerSeoTags();
      this.visibleTripId = new Date().getTime().toString();
      return;
    }

    const { savesId, startingLabel, name, plannerInfo } = this.trip();
    const visibleTripId = routeFirstChild.snapshot.paramMap.get('id');
    const setDynamicMetaTag = () => {
      const country: string = startingLabel.includes(',')
        ? startingLabel.split(',')[1].trim()
        : startingLabel;
      const dynamicTitle = `Plan your Trip to ${country} - ${name}`;
      this.#metaTagsConfigService.setMetaTagsDynamically(
        ERoutes.TRIP_PLANNER,
        `Plan your vacation in ${country} with RoutePerfect free trip planner. Easily create your ${country} trip!`,
        dynamicTitle,
      );
    };

    if (visibleTripId.includes('-')) {
      if (savesId !== visibleTripId) {
        this.#tripsService
          .getTripByUrlName('trip-planner/' + visibleTripId)
          .subscribe((res) => {
            if (res) {
              const { route, duration, blogUrl, name, description } = res;
              this.setTripData(route, duration, visibleTripId, blogUrl);
              this.#metaTagsConfigService.setMetaTagsDynamically(
                ERoutes.TRIP_PLANNER,
                description,
                name,
              );
              this.trip.update((trip) =>
                trip.updateTrip({
                  name: name,
                  plannerInfo: {
                    type: 'popular',
                    name: name,
                    description: description,
                  },
                  includes: [],
                  excludes: [],
                }),
              );
              this.updateTripState();
            }
          });
      } else {
        this.visibleTripId = new Date().getTime().toString();
        this.#metaTagsConfigService.setMetaTagsDynamically(
          ERoutes.TRIP_PLANNER,
          plannerInfo.description,
          plannerInfo.name,
        );
      }
    } else if (savesId !== visibleTripId) {
      this.#tripsService.getSavedTripById(visibleTripId).subscribe((res) => {
        if (res) {
          const { route, duration, name } = res;
          this.trip.update((trip) =>
            trip.updateTrip({
              name: name,
              plannerInfo: {
                type: 'classic',
                name: '',
                description: '',
              },
              includes: [],
              excludes: [],
            }),
          );
          this.updateTripState();
          this.setTripData(route, duration, visibleTripId);
          setDynamicMetaTag();
        }
      });
    } else {
      this.visibleTripId = new Date().getTime().toString();
      this.#metaTagsConfigService.removeNoIndexMetaTag();
    }
  }

  private setTripData(
    routes: ITripRoute[],
    duration: string,
    visibleTripId: string,
    blogUrl = '',
  ): void {
    const firstDestination = routes[0];
    const lastDestination = routes[routes.length - 1];

    this.trip.update((trip) =>
      trip.updateTrip({
        blogUrl: blogUrl,
        savesId: visibleTripId,
        isSpecifyDates: false,
        isOptimizedMode: false,
        startingLocationId: String(firstDestination.id),
        startingLabel: String(firstDestination.name),
        startingCountry: String(firstDestination.countryId),
        endingLocationId: String(lastDestination.id),
        endingLabel: String(lastDestination.name),
        endingCountry: String(lastDestination.countryId),
        numDays: Number(duration),
        route: routes,
      }),
    );
    this.updateTripState();
    this.initTripData();
    this.visibleTripId = visibleTripId;
    this.checkForWelcomeViewNeeded();
  }

  private updateTripState(): void {
    this.#tripWizardStateService.currentTripState = new Trip({
      ...this.trip(),
    });
  }
}
